/**
 * Connect Space Dialog
 *
 * A quiet, progressive-disclosure flow for connecting a Space to an
 * external source. This is intentionally niche: it lives behind a subtle
 * menu entry rather than a prominent button.
 *
 * Flow:
 *   Step "chooser"  — pick a connection type (only "Code repository (GitHub)"
 *                     today; room reserved for future types).
 *   Step "github"   — if the workspace has no GitHub App installed, show a
 *                     calm explainer + "Install Losi on GitHub"; otherwise
 *                     show a searchable repo picker + branch input and a
 *                     "Connect" action. Existing connections are listed with
 *                     a disconnect affordance.
 *
 * Primary responsibilities:
 * - Fetch/mutate space repo links via the /api/spaces/:id/repo-links routes
 * - Fetch accessible repos via /api/integrations/github-app/repos
 * - Handle loading / empty / error / needs-install states accessibly
 *
 * Dependencies:
 * - @/components/ui/* design system
 * - @tabler/icons-react
 * - framer-motion (reduced-motion aware)
 */

"use client"

import { FC, useCallback, useEffect, useMemo, useState } from "react"
import {
  IconBrandGithub,
  IconChevronRight,
  IconCheck,
  IconGitBranch,
  IconLink,
  IconPlug,
  IconPlus,
  IconTrash
} from "@tabler/icons-react"
import { AnimatePresence, motion, useReducedMotion } from "framer-motion"

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Separator } from "@/components/ui/separator"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "@/components/ui/select"
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList
} from "@/components/ui/command"
import { toast } from "sonner"
import { cn } from "@/lib/utils"
import type { SpaceRepoLink } from "@/db/space-repo-links"
import type { GithubRepoOption } from "@/app/api/integrations/github-app/repos/route"

/** Props for the connect flow. */
export interface ConnectSpaceDialogProps {
  spaceId: string
  workspaceId: string
  spaceName?: string
  open: boolean
  onOpenChange: (open: boolean) => void
}

/** Which step of the progressive flow is visible. */
type Step = "chooser" | "github"

type ReposResponse = {
  repos: GithubRepoOption[]
  needs_install: boolean
  installation_id: string | null
}

// --------------

/** A single connection type row in the chooser. */
const ConnectionTypeRow: FC<{
  icon: React.ReactNode
  title: string
  hint: string
  onSelect?: () => void
  disabled?: boolean
}> = ({ icon, title, hint, onSelect, disabled }) => (
  <button
    type="button"
    onClick={onSelect}
    disabled={disabled}
    className={cn(
      "border-border/60 group flex w-full items-center gap-3 rounded-xl border p-3 text-left outline-none",
      "focus-visible:ring-primary/40 focus-visible:ring-2 motion-safe:transition-colors",
      disabled
        ? "cursor-not-allowed opacity-60"
        : "hover:bg-muted/60 hover:border-border"
    )}
  >
    <span className="bg-muted text-foreground flex size-9 shrink-0 items-center justify-center rounded-lg">
      {icon}
    </span>
    <span className="min-w-0 flex-1">
      <span className="flex items-center gap-2">
        <span className="text-foreground truncate text-sm font-medium">
          {title}
        </span>
        {disabled ? (
          <Badge variant="muted" className="shrink-0">
            Soon
          </Badge>
        ) : null}
      </span>
      <span className="text-muted-foreground mt-0.5 block truncate text-xs">
        {hint}
      </span>
    </span>
    {!disabled ? (
      <IconChevronRight
        size={16}
        className="text-muted-foreground shrink-0 group-hover:translate-x-0.5 motion-safe:transition-transform"
        aria-hidden="true"
      />
    ) : null}
  </button>
)

// --------------

export const ConnectSpaceDialog: FC<ConnectSpaceDialogProps> = ({
  spaceId,
  workspaceId,
  spaceName,
  open,
  onOpenChange
}) => {
  const reduceMotion = useReducedMotion()
  const [step, setStep] = useState<Step>("chooser")

  const [links, setLinks] = useState<SpaceRepoLink[]>([])
  const [linksLoading, setLinksLoading] = useState(false)

  const [repos, setRepos] = useState<GithubRepoOption[]>([])
  const [installationId, setInstallationId] = useState<string | null>(null)
  const [needsInstall, setNeedsInstall] = useState(false)
  const [reposLoading, setReposLoading] = useState(false)
  const [reposError, setReposError] = useState<string | null>(null)

  const [selectedRepo, setSelectedRepo] = useState<GithubRepoOption | null>(null)
  const [branch, setBranch] = useState("")
  const [branches, setBranches] = useState<string[]>([])
  const [branchesLoading, setBranchesLoading] = useState(false)
  const [connecting, setConnecting] = useState(false)
  const [disconnectingId, setDisconnectingId] = useState<string | null>(null)
  // When links already exist, keep the repo picker hidden until the user
  // explicitly chooses to add/change a connection.
  const [showPicker, setShowPicker] = useState(false)

  // --------------

  const loadLinks = useCallback(async () => {
    setLinksLoading(true)
    try {
      const res = await fetch(`/api/spaces/${spaceId}/repo-links`)
      if (!res.ok) throw new Error("Failed to load connections")
      const data = (await res.json()) as { links: SpaceRepoLink[] }
      setLinks(data.links ?? [])
    } catch {
      // Non-fatal: show empty and let the user retry via reopening.
      setLinks([])
    } finally {
      setLinksLoading(false)
    }
  }, [spaceId])

  // --------------

  const loadRepos = useCallback(async () => {
    setReposLoading(true)
    setReposError(null)
    try {
      const res = await fetch(
        `/api/integrations/github-app/repos?workspace_id=${encodeURIComponent(workspaceId)}`
      )
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null
        throw new Error(body?.error ?? "Failed to load repositories")
      }
      const data = (await res.json()) as ReposResponse
      setNeedsInstall(data.needs_install)
      setRepos(data.repos ?? [])
      setInstallationId(data.installation_id)
    } catch (err) {
      setReposError(
        err instanceof Error ? err.message : "Failed to load repositories"
      )
      setRepos([])
    } finally {
      setReposLoading(false)
    }
  }, [workspaceId])

  // --------------

  // Reset transient state whenever the dialog opens, and hydrate links.
  useEffect(() => {
    if (!open) return
    setStep("chooser")
    setSelectedRepo(null)
    setBranch("")
    setShowPicker(false)
    void loadLinks()
  }, [open, loadLinks])

  // When a repo is selected, default the branch and fetch its real
  // branch list so the user picks from a dropdown, not free text.
  useEffect(() => {
    if (!selectedRepo) {
      setBranches([])
      return
    }
    setBranch(selectedRepo.default_branch)
    setBranchesLoading(true)
    const controller = new AbortController()
    fetch(
      `/api/integrations/github-app/branches?workspace_id=${encodeURIComponent(
        workspaceId
      )}&owner=${encodeURIComponent(selectedRepo.owner)}&repo=${encodeURIComponent(
        selectedRepo.name
      )}`,
      { signal: controller.signal }
    )
      .then(async (res) => {
        if (!res.ok) throw new Error("branches")
        const data = (await res.json()) as {
          branches: { name: string; is_default: boolean }[]
        }
        setBranches(data.branches.map((b) => b.name))
      })
      .catch(() => {
        // Fall back to just the default branch if listing fails.
        setBranches([selectedRepo.default_branch])
      })
      .finally(() => setBranchesLoading(false))
    return () => controller.abort()
  }, [selectedRepo, workspaceId])

  // --------------

  const handleChooseGithub = useCallback(() => {
    setStep("github")
    void loadRepos()
  }, [loadRepos])

  // --------------

  const handleInstall = useCallback(() => {
    const redirectUri = `/spaces/${spaceId}`
    window.location.href = `/api/integrations/github-app/install?workspace_id=${encodeURIComponent(
      workspaceId
    )}&redirect_uri=${encodeURIComponent(redirectUri)}`
  }, [spaceId, workspaceId])

  // --------------

  const handleConnect = useCallback(async () => {
    if (!selectedRepo) return
    if (!installationId) {
      toast.error("GitHub App installation not found. Try reinstalling.")
      return
    }
    setConnecting(true)
    try {
      const res = await fetch(`/api/spaces/${spaceId}/repo-links`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          github_installation_id: installationId,
          repo_owner: selectedRepo.owner,
          repo_name: selectedRepo.name,
          repo_full_name: selectedRepo.full_name,
          repo_id: selectedRepo.id,
          default_branch: selectedRepo.default_branch,
          branch_override:
            branch && branch !== selectedRepo.default_branch ? branch : null
        })
      })
      if (!res.ok) {
        const body = (await res.json().catch(() => null)) as
          | { error?: string }
          | null
        throw new Error(body?.error ?? "Failed to connect repository")
      }
      const data = (await res.json()) as { link: SpaceRepoLink }
      setLinks((prev) => [...prev, data.link])
      setSelectedRepo(null)
      setBranch("")
      setShowPicker(false)
      toast.success(`Connected ${data.link.repo_full_name}`)
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : "Failed to connect repository"
      )
    } finally {
      setConnecting(false)
    }
  }, [branch, selectedRepo, spaceId, installationId])

  // --------------

  const handleDisconnect = useCallback(
    async (linkId: string) => {
      setDisconnectingId(linkId)
      try {
        const res = await fetch(
          `/api/spaces/${spaceId}/repo-links/${linkId}`,
          { method: "DELETE" }
        )
        if (!res.ok && res.status !== 204) {
          throw new Error("Failed to disconnect")
        }
        setLinks((prev) => prev.filter((l) => l.id !== linkId))
        toast.success("Repository disconnected")
      } catch (err) {
        toast.error(err instanceof Error ? err.message : "Failed to disconnect")
      } finally {
        setDisconnectingId(null)
      }
    },
    [spaceId]
  )

  // --------------

  const connectedRepoIds = useMemo(
    () => new Set(links.map((l) => l.repo_id)),
    [links]
  )

  const availableRepos = useMemo(
    () => repos.filter((r) => !connectedRepoIds.has(r.id)),
    [repos, connectedRepoIds]
  )

  // --------------

  const motionProps = reduceMotion
    ? {}
    : {
        initial: { opacity: 0, y: 6 },
        animate: { opacity: 1, y: 0 },
        exit: { opacity: 0, y: -6 },
        transition: { duration: 0.16, ease: "easeOut" as const }
      }

  // --------------

  const renderConnectedLinks = () => {
    if (linksLoading || links.length === 0) return null
    return (
      <div className="space-y-2">
        <p className="text-muted-foreground text-xs font-medium">Connected</p>
        <div className="space-y-1.5">
          {links.map((link) => (
            <div
              key={link.id}
              className="border-border/60 bg-muted/30 flex items-center gap-3 rounded-lg border p-2.5"
            >
              <IconBrandGithub
                size={16}
                className="text-muted-foreground shrink-0"
                aria-hidden="true"
              />
              <span className="min-w-0 flex-1">
                <span className="text-foreground block truncate text-sm font-medium">
                  {link.repo_full_name}
                </span>
                <span className="text-muted-foreground flex items-center gap-1 text-xs">
                  <IconGitBranch size={12} aria-hidden="true" />
                  {link.branch_override ?? link.default_branch}
                </span>
              </span>
              <Button
                variant="ghost"
                size="sm"
                loading={disconnectingId === link.id}
                onClick={() => handleDisconnect(link.id)}
                aria-label={`Disconnect ${link.repo_full_name}`}
                className="text-muted-foreground hover:text-destructive shrink-0"
              >
                <IconTrash size={15} />
              </Button>
            </div>
          ))}
        </div>
      </div>
    )
  }

  // --------------

  const renderGithubStep = () => {
    if (reposLoading) {
      return (
        <div className="space-y-2" aria-busy="true">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="bg-muted/60 h-11 animate-pulse rounded-lg"
            />
          ))}
        </div>
      )
    }

    if (reposError) {
      return (
        <div className="border-border/60 rounded-xl border p-4 text-center">
          <p className="text-foreground text-sm">{reposError}</p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void loadRepos()}
            className="mt-3"
          >
            Try again
          </Button>
        </div>
      )
    }

    if (needsInstall) {
      return (
        <div className="border-border/60 bg-muted/30 rounded-xl border p-5 text-center">
          <span className="bg-background mx-auto flex size-10 items-center justify-center rounded-full">
            <IconBrandGithub size={20} aria-hidden="true" />
          </span>
          <p className="text-foreground mt-3 text-sm font-medium">
            Install Losi on GitHub
          </p>
          <p className="text-muted-foreground mx-auto mt-1 max-w-xs text-xs">
            To connect a repository, install the Losi GitHub App and grant
            access to the repos you want this Space to work with.
          </p>
          <Button onClick={handleInstall} className="mt-4" size="sm">
            <IconBrandGithub size={16} />
            Install Losi on GitHub
          </Button>
        </div>
      )
    }

    // Already connected and not actively adding: show the connected repo(s)
    // and a quiet "Add another repository" affordance — no picker clutter.
    const hasLinks = links.length > 0
    const pickerVisible = showPicker || !hasLinks

    return (
      <div className="space-y-4">
        {renderConnectedLinks()}

        {hasLinks && !pickerVisible ? (
          <Button
            variant="outline"
            size="sm"
            className="w-full gap-2"
            onClick={() => setShowPicker(true)}
          >
            <IconPlus size={15} />
            Add another repository
          </Button>
        ) : null}

        {pickerVisible ? (
          <>
            {hasLinks ? <Separator className="opacity-60" /> : null}
            <div className="space-y-2">
              <p className="text-muted-foreground text-xs font-medium">
                {selectedRepo ? "Selected repository" : "Choose a repository"}
              </p>

              {selectedRepo ? (
                <div className="border-primary/40 bg-primary/5 flex items-center gap-3 rounded-lg border p-2.5">
                  <IconCheck
                    size={16}
                    className="text-primary shrink-0"
                    aria-hidden="true"
                  />
                  <span className="text-foreground min-w-0 flex-1 truncate text-sm font-medium">
                    {selectedRepo.full_name}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setSelectedRepo(null)}
                    className="shrink-0"
                  >
                Change
              </Button>
            </div>
          ) : availableRepos.length === 0 ? (
            <div className="border-border/60 text-muted-foreground rounded-lg border p-4 text-center text-sm">
              No more repositories available to connect.
            </div>
          ) : (
            <Command className="border-border/60 rounded-lg border">
              <CommandInput placeholder="Search repositories..." />
              <CommandList className="max-h-56">
                <CommandEmpty>No repositories found.</CommandEmpty>
                {availableRepos.map((repo) => (
                  <CommandItem
                    key={repo.id}
                    value={repo.full_name}
                    onSelect={() => setSelectedRepo(repo)}
                    className="gap-2"
                  >
                    <IconBrandGithub
                      size={15}
                      className="text-muted-foreground shrink-0"
                      aria-hidden="true"
                    />
                    <span className="min-w-0 flex-1 truncate">
                      {repo.full_name}
                    </span>
                    {repo.private ? (
                      <Badge variant="muted" className="shrink-0">
                        Private
                      </Badge>
                    ) : null}
                  </CommandItem>
                ))}
              </CommandList>
                </Command>
              )}
            </div>

            {selectedRepo ? (
              <div className="space-y-2">
                <label
                  htmlFor="repo-branch"
                  className="text-muted-foreground flex items-center gap-1.5 text-xs font-medium"
                >
                  <IconGitBranch size={13} aria-hidden="true" />
                  Branch
                </label>
                <Select
                  value={branch}
                  onValueChange={setBranch}
                  disabled={branchesLoading}
                >
                  <SelectTrigger id="repo-branch" aria-label="Branch to connect">
                    <SelectValue
                      placeholder={
                        branchesLoading
                          ? "Loading branches…"
                          : selectedRepo.default_branch
                      }
                    />
                  </SelectTrigger>
                  <SelectContent className="max-h-60">
                    {branches.map((b) => (
                      <SelectItem key={b} value={b}>
                        {b}
                        {b === selectedRepo.default_branch ? " (default)" : ""}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
          </>
        ) : null}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              // If we're mid-add with existing links, cancel back to the
              // connected view instead of leaving the GitHub step entirely.
              if (showPicker && links.length > 0) {
                setShowPicker(false)
                setSelectedRepo(null)
              } else {
                setStep("chooser")
              }
            }}
          >
            Back
          </Button>
          {pickerVisible ? (
            <Button
              size="sm"
              loading={connecting}
              disabled={!selectedRepo || !branch.trim()}
              onClick={() => void handleConnect()}
            >
              <IconLink size={16} />
              Connect
            </Button>
          ) : (
            <Button size="sm" onClick={() => onOpenChange(false)}>
              <IconCheck size={16} />
              Done
            </Button>
          )}
        </div>
      </div>
    )
  }

  // --------------

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[85vh] max-w-md flex-col gap-0 overflow-hidden p-0">
        <DialogHeader className="shrink-0 p-6 pb-3">
          <DialogTitle className="flex items-center gap-2 text-base">
            <IconPlug size={18} aria-hidden="true" />
            {step === "chooser"
              ? "Connect this Space to a source"
              : "Connect a code repository"}
          </DialogTitle>
          <DialogDescription>
            {step === "chooser"
              ? spaceName
                ? `Link ${spaceName} to an external source.`
                : "Link this Space to an external source."
              : "Pick a repository and branch for this Space to work with."}
          </DialogDescription>
        </DialogHeader>

        {/* Scrollable body: grows between header/footer, scrolls vertically,
            never horizontally. */}
        <div className="min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-6 pb-6">
          <AnimatePresence mode="wait" initial={false}>
            {step === "chooser" ? (
              <motion.div key="chooser" {...motionProps} className="space-y-2">
                <ConnectionTypeRow
                  icon={<IconBrandGithub size={18} aria-hidden="true" />}
                  title="Code repository (GitHub)"
                  hint="Connect a GitHub repo so Losi can read and work with your code."
                  onSelect={handleChooseGithub}
                />
                <ConnectionTypeRow
                  icon={<IconPlus size={18} aria-hidden="true" />}
                  title="More connection types"
                  hint="Additional sources are on the way."
                  disabled
                />
              </motion.div>
            ) : (
              <motion.div key="github" {...motionProps}>
                {renderGithubStep()}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </DialogContent>
    </Dialog>
  )
}

export default ConnectSpaceDialog
